import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, 
  Badge, Spinner, Flex, Button, Input, InputGroup, 
  InputLeftElement, Icon, useToast
} from '@chakra-ui/react';
import { FaArrowLeft, FaSearch, FaUser, FaSignInAlt, FaCalendarAlt } from 'react-icons/fa';
import supabase from '../utils/supabaseUtils';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch users with login count
      const { data, error } = await supabase
        .rpc('get_users_with_login_count')
        .order('last_login_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setUsers(data);
      } else {
        // Fallback to fetching just users if the RPC isn't available
        const { data: usersData, error: usersError } = await supabase
          .from('app_users')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (usersError) throw usersError;
        
        // Add default login count if not available
        setUsers(usersData.map(user => ({
          ...user, 
          login_count: 0,
          last_login_at: user.created_at
        })));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error fetching users',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.user_type && user.user_type.toLowerCase().includes(searchLower))
    );
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Invalid date';
    }
  };

  return (
    <Box p={5}>
      <Flex mb={5} alignItems="center">
        <Link to="/dashboard">
          <Button leftIcon={<FaArrowLeft />} variant="outline" mr={4}>
            Back to Dashboard
          </Button>
        </Link>
        <Heading size="lg">User Management</Heading>
      </Flex>

      <Flex justifyContent="space-between" mb={5} flexWrap="wrap">
        <Box mb={{ base: 3, md: 0 }}>
          <Text fontSize="lg" mb={1}>Total Users: {users.length}</Text>
          <Text fontSize="sm" color="gray.600">
            Manage user accounts and view login activity
          </Text>
        </Box>
        
        <InputGroup maxW={{ base: "100%", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </Flex>
      
      {loading ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" thickness="4px" color="blue.500" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>User</Th>
                <Th>Email</Th>
                <Th>User Type</Th>
                <Th>Login Count</Th>
                <Th>Last Login</Th>
                <Th>Joined</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <Tr key={user.id}>
                    <Td>
                      <Flex align="center">
                        <Box
                          bg="blue.500"
                          color="white"
                          borderRadius="full"
                          w="40px"
                          h="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mr={3}
                        >
                          <Icon as={FaUser} />
                        </Box>
                        <Box>
                          <Text fontWeight="bold">{user.first_name} {user.last_name}</Text>
                          {user.is_admin && (
                            <Badge colorScheme="purple" mt={1}>Admin</Badge>
                          )}
                        </Box>
                      </Flex>
                    </Td>
                    <Td>{user.email}</Td>
                    <Td>
                      <Badge
                        colorScheme={
                          user.user_type === 'admin' ? 'red' :
                          user.user_type === 'staff' ? 'orange' : 'green'
                        }
                      >
                        {user.user_type}
                      </Badge>
                    </Td>
                    <Td>
                      <Flex align="center">
                        <Icon as={FaSignInAlt} mr={2} color="blue.500" />
                        {user.login_count || 0}
                      </Flex>
                    </Td>
                    <Td>
                      <Flex align="center">
                        <Icon as={FaCalendarAlt} mr={2} color="blue.500" />
                        {formatDate(user.last_login_at)}
                      </Flex>
                    </Td>
                    <Td>{formatDate(user.created_at)}</Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={10}>
                    <Text>No users found matching "{searchQuery}"</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default UserManagement; 